import { Box, Divider, Heading, TextButton } from '@wix/design-system'
import { ChoiceQuestionContentComponent, GroupContentComponent, useContent, useNext } from '@wix/funnel-builder-lizard'
import { ArrowRight } from '@wix/wix-ui-icons-common'
import React, { FC, useCallback, useEffect } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { BackButton } from '../../reusable/BackButton'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { CardBox } from './CardBox'
import { customizeSiteEqualSplitDataHooks as dataHooks } from './CustomizeSiteEqualSplit.dataHooks'
import s from './CustomizeSiteEqualSplit.scss'

export type EqualSplitOption = {
  img: string
  title: string
  description: string
  button: string
  link: string
  badge?: string
}

export type CustomizeSiteEqualSplitContent = {
  platform: ChoiceQuestionContentComponent<{ title: string }, EqualSplitOption>
  back: ChoiceQuestionContentComponent<{}, { title: string; isBrowserBack: boolean }>
  linkButton?: GroupContentComponent<{ label: string; biAction: string }>
}

export const CustomizeSiteEqualSplit: FC = () => {
  const { content } = useContent<CustomizeSiteEqualSplitContent>()
  const { title } = content.platform.content
  const [backButton] = content.back.options

  const next = useNext()

  const pageLogger = usePageLogger()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleOptionClick = (optionKey: string) => {
    void pageLogger.answer({ answer: optionKey })
    next({
      [content.platform.questionKey]: {
        values: [optionKey],
      },
    })
  }

  const handleBackClick = useCallback(() => {
    if (!backButton) {
      return
    }

    if (backButton.isBrowserBack) {
      void pageLogger.back()

      window.history.back()
      return
    }

    void pageLogger.generalAction({ action: 'back' })
    next({
      [content.back.questionKey]: {
        values: [backButton.optionKey],
      },
    })
  }, [backButton, pageLogger, content.back.questionKey, next])

  const handleLinkButtonClick = () => {
    void pageLogger.generalAction({ action: content.linkButton!!.content.biAction })
    next({
      [content.back.questionKey]: {
        values: [backButton.optionKey],
      },
    })
  }

  const cardOptions = content.platform.options.filter(({ link }) => !link)
  const linkOption = content.platform.options.find(({ link }) => !!link)

  return (
    <Box width="100%">
      <Box height="42px" verticalAlign="middle" gap="18px" position="absolute" left="36px" top="36px">
        <WixLogo />
        <Divider direction="vertical" />
        {backButton && (
          <BackButton text={backButton.title} onClick={handleBackClick} dataHook={dataHooks.backButton()} />
        )}
      </Box>
      <Box direction="vertical" gap="SP7" margin="198px auto 0 auto" textAlign="center">
        <Heading className={s.title} dataHook={dataHooks.title()}>
          {title}
        </Heading>
        <Box gap="SP8" direction="vertical" align="center">
          <Box gap="SP7">
            {cardOptions.map(({ img, title, description, button, badge, optionKey }) => (
              <CardBox
                key={optionKey}
                imageUrl={img}
                title={title}
                description={description}
                buttonText={button}
                badge={badge}
                onClick={() => handleOptionClick(optionKey)}
                dataHook={dataHooks.cardBox({ id: optionKey })}
              />
            ))}
          </Box>
          {linkOption && !content.linkButton?.content.label && (
            <TextButton
              underline="always"
              onClick={() => handleOptionClick(linkOption.optionKey)}
              dataHook={dataHooks.linkOption()}
            >
              {linkOption.link}
            </TextButton>
          )}
          {content.linkButton?.content.label && (
            <TextButton
              onClick={() => handleLinkButtonClick()}
              suffixIcon={<ArrowRight />}
              dataHook={dataHooks.linkButton()}
            >
              {content.linkButton.content.label}
            </TextButton>
          )}
        </Box>
      </Box>
    </Box>
  )
}
